import { MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type { ValueSet } from '@medplum/fhirtypes';

/**
 * RxNorm ingredient sets for the drug classes clinical protocols branch on ("is the patient on a
 * statin?", "on an agent that can cause hypoglycemia?"). Membership was taken from RxNav's RxClass
 * (`/rxclass/classMembers?relaSource=ATC`) for the ATC class named on each set, on 2026-09-05, and
 * is reproduced verbatim — including agents rarely or no longer prescribed — so a match never depends
 * on an editorial choice here. Matching is on the ingredient RxCUI: a prescription carries a product
 * code, which the caller resolves to its ingredients before planning, so an ingredient-level set is
 * what both sides compare on.
 */
interface RxNormSetBase {
  name: string;
  title: string;
  description: string;
  concepts: ReadonlyArray<{ code: string; display: string }>;
}

/**
 * Every set records where its membership came from, and the two ways of saying so are mutually
 * exclusive: a set either *is* an ATC class, or it is a curated list that no single class collects
 * and has to say why. Writing this as a union rather than two optional fields makes omitting both a
 * compile error — otherwise the description silently renders "… undefined".
 */
type RxNormClassSpec = RxNormSetBase &
  (
    | { atc: string; provenance?: never }
    /** See `csDmardsValueSet`, the one set here whose agents ATC does not collect. */
    | { atc?: never; provenance: string }
  );

const buildValueSet = (spec: RxNormClassSpec): ValueSet => {
  const url = `https://liza.health/fhir/ValueSet/${spec.name}`;
  const source = spec.atc ? `RxNorm ingredients in ATC class ${spec.atc}, per RxNav RxClass.` : spec.provenance;
  return {
    resourceType: 'ValueSet',
    name: spec.name,
    title: spec.title,
    url,
    identifier: [{ system: 'urn:ietf:rfc:3986', value: url }],
    version: '1',
    status: 'active',
    description: `${spec.description} ${source}`,
    compose: {
      include: [{ system: MEDICATION_CODE_SYSTEMS.RXNORM, concept: spec.concepts.map((c) => ({ ...c })) }],
    },
  };
};

export const statinsValueSet = buildValueSet({
  name: 'rxnorm-statins',
  title: 'Statins (HMG-CoA reductase inhibitors)',
  atc: 'C10AA',
  description: 'Lipid-lowering statin ingredients.',
  concepts: [
    { code: '301542', display: 'rosuvastatin' },
    { code: '36567', display: 'simvastatin' },
    { code: '41127', display: 'fluvastatin' },
    { code: '42463', display: 'pravastatin' },
    { code: '596723', display: 'cerivastatin' },
    { code: '6472', display: 'lovastatin' },
    { code: '83367', display: 'atorvastatin' },
    { code: '861634', display: 'pitavastatin' },
  ],
});

export const insulinsValueSet = buildValueSet({
  name: 'rxnorm-insulins',
  title: 'Insulins and analogues',
  atc: 'A10A',
  description: 'Insulin ingredients, including fixed-dose combinations that contain an insulin.',
  concepts: [
    { code: '139825', display: 'insulin detemir' },
    { code: '1670007', display: 'insulin degludec' },
    { code: '1727493', display: 'insulin degludec / liraglutide' },
    { code: '1858994', display: 'insulin glargine / lixisenatide' },
    { code: '221109', display: 'insulin, regular, pork' },
    { code: '253182', display: 'insulin, regular, human' },
    { code: '2740394', display: 'insulin icodec' },
    { code: '274783', display: 'insulin glargine' },
    { code: '400008', display: 'insulin glulisine, human' },
    { code: '51428', display: 'insulin aspart, human' },
    { code: '86009', display: 'insulin lispro' },
  ],
});

export const sulfonylureasValueSet = buildValueSet({
  name: 'rxnorm-sulfonylureas',
  title: 'Sulfonylureas',
  atc: 'A10BB',
  description: 'Sulfonylurea ingredients — the non-insulin agents that can cause hypoglycemia.',
  concepts: [
    { code: '102846', display: 'glibornuride' },
    { code: '10633', display: 'tolazamide' },
    { code: '10635', display: 'tolbutamide' },
    { code: '173', display: 'acetohexamide' },
    { code: '2068', display: 'carbutamide' },
    { code: '2404', display: 'chlorpropamide' },
    { code: '25789', display: 'glimepiride' },
    { code: '25793', display: 'gliquidone' },
    { code: '4815', display: 'glyburide' },
    { code: '4816', display: 'gliclazide' },
    { code: '4821', display: 'glipizide' },
  ],
});

/**
 * Steroidal MRAs only. The HF potassium schedule — about a week, then four weeks, then every six
 * months after initiating or intensifying — is written for spironolactone and eplerenone; ACE
 * inhibitors and ARNi carry only qualitative cautions with no interval, so extending it to them would
 * schedule a draw the guideline does not ask for.
 *
 * Finerenone is in this ATC class but is a *nonsteroidal* MRA, and KDIGO gives it a different schedule
 * (one month after initiation, then every four months). It is listed here because the class is
 * reproduced verbatim; a protocol that needs the distinction must condition on the ingredient rather
 * than on class membership alone.
 */
export const mineralocorticoidAntagonistsValueSet = buildValueSet({
  name: 'rxnorm-mineralocorticoid-antagonists',
  title: 'Mineralocorticoid receptor antagonists',
  atc: 'C03DA',
  description: 'Aldosterone antagonist ingredients, steroidal and nonsteroidal.',
  concepts: [
    { code: '1981', display: 'canrenoate potassium' },
    { code: '1982', display: 'canrenone' },
    { code: '2562811', display: 'finerenone' },
    { code: '298869', display: 'eplerenone' },
    { code: '9997', display: 'spironolactone' },
  ],
});

/** ACE inhibitors — half of what the guidelines call RASi; see `angiotensinReceptorBlockersValueSet`. */
export const aceInhibitorsValueSet = buildValueSet({
  name: 'rxnorm-ace-inhibitors',
  title: 'ACE inhibitors',
  atc: 'C09A',
  description: 'Angiotensin-converting enzyme inhibitor ingredients.',
  concepts: [
    { code: '18867', display: 'benazepril' },
    { code: '1998', display: 'captopril' },
    { code: '21102', display: 'cilazapril' },
    { code: '29046', display: 'lisinopril' },
    { code: '30131', display: 'moexipril' },
    { code: '35208', display: 'quinapril' },
    { code: '35296', display: 'ramipril' },
    { code: '36908', display: 'spirapril' },
    { code: '3827', display: 'enalapril' },
    { code: '38454', display: 'trandolapril' },
    { code: '39990', display: 'zofenopril' },
    { code: '50166', display: 'fosinopril' },
    { code: '54552', display: 'perindopril' },
    { code: '60245', display: 'imidapril' },
  ],
});

/** ARBs — the other half of RASi. KDIGO's 2-4 week post-initiation check applies to both. */
export const angiotensinReceptorBlockersValueSet = buildValueSet({
  name: 'rxnorm-angiotensin-receptor-blockers',
  title: 'Angiotensin II receptor blockers',
  atc: 'C09C',
  description: 'Angiotensin II receptor blocker ingredients.',
  concepts: [
    { code: '1091642', display: 'azilsartan medoxomil' },
    { code: '118463', display: 'olmesartan medoxomil' },
    { code: '214354', display: 'candesartan' },
    { code: '52175', display: 'losartan' },
    { code: '69749', display: 'valsartan' },
    { code: '73494', display: 'telmisartan' },
    { code: '83515', display: 'eprosartan' },
    { code: '83818', display: 'irbesartan' },
  ],
});

/**
 * Direct factor Xa inhibitors. The AF renal, liver and haematology intervals are DOAC-gated, and this
 * is the DOAC class that matters for them — dabigatran is a direct thrombin inhibitor in a different
 * ATC class and is not here.
 */
export const factorXaInhibitorsValueSet = buildValueSet({
  name: 'rxnorm-factor-xa-inhibitors',
  title: 'Direct factor Xa inhibitors',
  atc: 'B01AF',
  description: 'Direct oral factor Xa inhibitor ingredients.',
  concepts: [
    { code: '1114195', display: 'rivaroxaban' },
    { code: '1364430', display: 'apixaban' },
    { code: '1599538', display: 'edoxaban' },
    { code: '1927851', display: 'betrixaban' },
  ],
});

/** Vitamin K antagonists — what "on warfarin" means for the AF INR monitoring recommendation. */
export const vitaminKAntagonistsValueSet = buildValueSet({
  name: 'rxnorm-vitamin-k-antagonists',
  title: 'Vitamin K antagonists',
  atc: 'B01AA',
  description: 'Vitamin K antagonist anticoagulant ingredients.',
  concepts: [
    { code: '11289', display: 'warfarin' },
    { code: '154', display: 'acenocoumarol' },
    { code: '1598', display: 'dicumarol' },
    { code: '163426', display: 'tioclomarol' },
    { code: '50097', display: 'fluindione' },
    { code: '8130', display: 'phenindione' },
    { code: '8150', display: 'phenprocoumon' },
  ],
});

/**
 * Class III antiarrhythmics. AF gives amiodarone its own six-monthly TSH and aminotransferase
 * schedule, and dofetilide and sotalol a three-to-six-monthly ECG and electrolyte one — different
 * agents in and around this class with different monitoring, so a protocol conditions on the
 * ingredient rather than on class membership. Sotalol is a beta blocker (C07AA) and is not in this
 * class despite its class III activity.
 */
export const classIiiAntiarrhythmicsValueSet = buildValueSet({
  name: 'rxnorm-class-iii-antiarrhythmics',
  title: 'Class III antiarrhythmics',
  atc: 'C01BD',
  description: 'Class III antiarrhythmic ingredients.',
  concepts: [
    { code: '1737', display: 'bretylium tosylate' },
    { code: '233698', display: 'dronedarone' },
    { code: '41289', display: 'ibutilide' },
    { code: '49247', display: 'dofetilide' },
    { code: '703', display: 'amiodarone' },
  ],
});

/**
 * The conventional synthetic DMARDs ACR's lab-monitoring schedule is written for.
 *
 * Unlike every other set here this is not an ATC class, because no single class collects these four:
 * methotrexate sits in L04AX with thalidomide and the fumarates, leflunomide in L04AA, sulfasalazine
 * in A07EC beside mesalamine, and hydroxychloroquine among the antimalarials. Reproducing any one
 * class verbatim would pull in agents ACR's schedule says nothing about, so membership here is the
 * four agents the guideline names.
 *
 * The schedule differs *within* the set: methotrexate, leflunomide and sulfasalazine share the
 * three-phase CBC, aminotransferase and creatinine monitoring, while hydroxychloroquine is baseline
 * only. A protocol needs the ingredient, not just membership.
 */
export const csDmardsValueSet = buildValueSet({
  name: 'rxnorm-cs-dmards',
  title: 'Conventional synthetic DMARDs',
  provenance:
    'RxNorm ingredients for the four agents named in the ACR rheumatoid arthritis lab-monitoring schedule; not an ATC class, which does not collect them.',
  description: 'Conventional synthetic disease-modifying antirheumatic drug ingredients.',
  concepts: [
    { code: '27169', display: 'leflunomide' },
    { code: '5521', display: 'hydroxychloroquine' },
    { code: '6851', display: 'methotrexate' },
    { code: '9524', display: 'sulfasalazine' },
  ],
});

/** Urate-lowering therapy — what gout serum-urate monitoring is gated on. */
export const urateLoweringTherapyValueSet = buildValueSet({
  name: 'rxnorm-urate-lowering-therapy',
  title: 'Urate-lowering therapy (xanthine oxidase inhibitors)',
  atc: 'M04AA',
  description: 'Xanthine oxidase inhibitor ingredients.',
  concepts: [
    { code: '38136', display: 'tisopurine' },
    { code: '519', display: 'allopurinol' },
    { code: '73689', display: 'febuxostat' },
  ],
});

/** The RxCUIs in a drug-class ValueSet, for binding as a protocol-condition variable. */
export const valueSetRxcuis = (valueSet: ValueSet): string[] =>
  (valueSet.compose?.include ?? []).flatMap((inc) => (inc.concept ?? []).map((c) => c.code!));
